import {useCallback, useEffect, useMemo, useState} from "react";
import FolderView from "./components/FolderView";
import Loader from "components/Loader";
import {FileSystemItem} from "types";
import api from "api/axios";
import {handleAxiosAndCaughtError} from "utils";
import {useLibraryContext} from "../context/LibraryContext";
import {useNavigate, useSearchParams} from "react-router-dom";
import {Dialog} from "components/dialog";
import FileView from "./components/FileView";
import toast from "react-hot-toast";
import WorkflowDialog, {Workflow,} from "../../workflow/components/WorkflowDialog";
import {useConfirmation} from "components/dialog/confirmation";
import DeepExtractFileView from "./components/DeepExtractFileView";
import {useTranslation} from "react-i18next";
import {Library} from "constants/library";

const FileOrFolderView = () => {
  const { t } = useTranslation();
  const {
    currentFolderId,
    breadcrumb,
    setCurrentFolderId,
    isWorkflowLimitReached,
    refreshUsageData,
  } = useLibraryContext();
  const [files, setFiles] = useState<FileSystemItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [viewMode, setViewMode] = useState<"grid" | "list">("list");
  const [sortBy, setSortBy] = useState<"name" | "size">("name");
  const [sortOrder, setSortOrder] = useState<"asc" | "desc">("asc");
  const [searchParams, setSearchParams] = useSearchParams();
  const fileId = searchParams.get("file");
  const [dialogOpen, setDialogOpen] = useState(!!fileId);
  const deepExtract = searchParams.get("deepExtract");
  const [deepExtractDialogOpen, setDeepExtractDialogOpen] = useState(
    !!deepExtract
  );
  const [workflowId, setWorkflowId] = useState<string | null>(null);
  const [isWorkflowDialogOpen, setWorkflowDialogOpen] = useState(false);
  const [workflowToEdit, setWorkflowToEdit] = useState<Workflow | null>(null);
  const [workflowLoading, setWorkflowLoading] = useState(false);

  const { confirm } = useConfirmation();
  const navigate = useNavigate();
  // Keep dialogOpen in sync with fileId
  useEffect(() => {
    setDialogOpen(!!fileId);
  }, [fileId]);

  useEffect(() => {
    setDeepExtractDialogOpen(!!deepExtract);
  }, [deepExtract]);

  useEffect(() => {
    if (workflowId) {
      const fetchWorkflowDetails = async () => {
        setWorkflowLoading(true);
        try {
          const response = await api.get(`/workflows/${workflowId}`);
          const workflowData = response.data;
          let steps = [];
          if (workflowData.content) {
            try {
              steps = workflowData.content;
            } catch (e) {
              console.error("Failed to parse workflow content", e);
              toast.error(t("errors.generic"));
              steps = [];
            }
          }
          const workflowDetails: Workflow = {
            id: workflowData.id,
            name: workflowData.name,
            description: workflowData.description,
            content: steps,
          };
          setWorkflowToEdit(workflowDetails);
          setWorkflowDialogOpen(true);
        } catch (err) {
          handleAxiosAndCaughtError(err);
          toast.error(t("errors.generic"));
          setWorkflowId(null); // Reset on error
        } finally {
          setWorkflowLoading(false);
        }
      };

      fetchWorkflowDetails();
    }
  }, [workflowId]);

  const fetchFiles = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      // Check if the current folder is the root "Workflows" folder based on breadcrumb
      const isWorkflowsRootFolder =
        breadcrumb.length === 1 && breadcrumb[0].typeName === Library.WORKFLOWS;
      let itemsResponse;
      if (isWorkflowsRootFolder) {
        itemsResponse = await api.get(`/folders/${currentFolderId}/workflows`);
      } else {
        if (currentFolderId) {
          itemsResponse = await api.get(
            `/folders/${currentFolderId}/documents`
          );
        } else {
          setCurrentFolderId("/");
          navigate(`/dashboard/library`);
          return;
        }
        // Otherwise, fetch documents
      }

      setFiles(itemsResponse.data);
    } catch (err) {
      setError(t("library.errorFetchingFiles"));
      handleAxiosAndCaughtError(err);
    } finally {
      setLoading(false);
    }
  }, [currentFolderId, breadcrumb, navigate, setCurrentFolderId]);

  useEffect(() => {
    fetchFiles();
  }, [fetchFiles]);

  const sortedItems = useMemo(() => {
    if (files.length <= 0) return [];
    const sorted = [...files].sort((a, b) => {
      let compareValue = 0;
      if (sortBy === "name") {
        compareValue = a.name.localeCompare(b.name);
      } else if (sortBy === "size") {
        // If either is a folder, treat as 0 for size
        const aSize =
          a.type === "Document"
            ? Number(a.document_metadata.file_size) || 0
            : 0;
        const bSize =
          b.type === "Document"
            ? Number(b.document_metadata.file_size) || 0
            : 0;
        compareValue = aSize - bSize;
      }
      return sortOrder === "asc" ? compareValue : -compareValue;
    });

    // Folders first, then documents
    return sorted.sort((a, b) => {
      const typeOrder = { Folder: 0, Workflow: 1, Document: 2 };

      const getFileItemType = (item: FileSystemItem) => {
        if (item.type === "Folder") return "Folder";
        if (item.type === "Workflow") return "Workflow";
        return "Document";
      };

      const aType = getFileItemType(a);
      const bType = getFileItemType(b);

      if (typeOrder[aType] < typeOrder[bType]) return -1;
      if (typeOrder[aType] > typeOrder[bType]) return 1;

      return 0;
    });
  }, [files, sortBy, sortOrder]);

  // Resolve a file to show in dialogs
  const getFileToShow = useCallback(
    (idOverride?: string | null) => {
      const idToFind = idOverride || fileId;
      if (!idToFind) return null;
      return files.find((f) => f.id === idToFind) || null;
    },
    [fileId, files]
  );

  const handleDialogClose = (deleteString?: string) => {
    setTimeout(() => {
      setSearchParams((params) => {
        params.delete(deleteString ?? "file");
        return params;
      });
    }, 200);
  };

  const handleWorkflowDialogClose = () => {
    setWorkflowId(null);
    setWorkflowDialogOpen(false);
    setWorkflowToEdit(null);
  };

  const handleDeleteWorkflow = async (workflowIdToDelete: string) => {
    const confirmed = await confirm({
      title: t("library.deleteWorkflow"),
      message: t("library.deleteWorkflowConfirm"),
      confirmText: t("common.delete"),
      cancelText: t("common.cancel"),
      variant: "danger",
    });

    if (confirmed) {
      try {
        await api.delete(`/workflows/${workflowIdToDelete}`);
        toast.success(t("library.workflowDeletedSuccessfully"));
        await fetchFiles();
        // Refresh usage data after deletion
        await refreshUsageData();
      } catch (err) {
        handleAxiosAndCaughtError(err);
      }
    }
  };

  const handleWorkflowSubmit = async (data: Partial<Workflow>) => {
    try {
      if (workflowId) {
        // Updating existing workflow - no limit check needed
        await api.put(`/workflows/${workflowId}`, {
          name: data.name,
          description: data.description,
          content: data.content,
        });
        toast.success(t("library.workflowUpdatedSuccessfully"));
      } else {
        // Creating new workflow - check limit first
        if (isWorkflowLimitReached) {
          toast.error(t("library.workflowLimitReached"));
          return;
        }

        const payload = {
          name: data.name,
          description: data.description,
          content: data.content,
          folder_id: currentFolderId,
        };

        const res = await api.post("/workflows", payload);

        // Check if the response indicates an error
        if (!res || (res.status && res.status >= 400)) {
          const errorMessage =
            res?.data?.detail ||
            res?.data?.message ||
            "Failed to create workflow";
          // Check if it's a limit error
          if (
            errorMessage.toLowerCase().includes("limit") ||
            res?.status === 403
          ) {
            toast.error(t("library.workflowLimitReached"));
            await refreshUsageData();
          } else {
            toast.error(errorMessage);
          }
          return;
        }

        toast.success(t("library.workflowCreatedSuccessfully"));
      }
      await fetchFiles();
      // Refresh usage data after creation/update
      await refreshUsageData();
      handleWorkflowDialogClose();
    } catch (err: any) {
      // Catch block only executes for actual exceptions
      if (
        err?.response?.status === 403 ||
        err?.response?.data?.message?.toLowerCase().includes("limit")
      ) {
        toast.error(t("library.workflowLimitReached"));
        await refreshUsageData();
      } else {
        handleAxiosAndCaughtError(err);
      }
    }
  };

  if (loading) {
    return (
      <Loader
        txt={t("library.gettingItems")}
        className="absolute left-1/2 top-1/2  -translate-y-1/2 translate-x-1/2"
      />
    );
  }

  if (error) {
    return (
      <div className="absolute left-1/2 top-1/2 flex  -translate-y-1/2  translate-x-1/2 flex-col  items-center justify-center gap-2  ">
        <p className="text-lg font-medium text-red-500">{error}</p>
        <button
          onClick={fetchFiles}
          className="rounded-lg bg-blue-500 px-4 py-1 text-white hover:bg-blue-600"
        >
          {t("library.retry")}
        </button>
      </div>
    );
  }
  const handleSort = (by: "name" | "size") => {
    if (sortBy === by) {
      setSortOrder(sortOrder === "asc" ? "desc" : "asc");
    } else {
      setSortBy(by);
      setSortOrder("asc");
    }
  };

  return (
    <>
      <FolderView
        sortedItems={sortedItems}
        viewMode={viewMode}
        fetchFiles={fetchFiles}
        sortOrder={sortOrder}
        sortBy={sortBy}
        onSort={handleSort}
        onViewModeChange={setViewMode}
        onWorkflowClick={(id: string) => {
          setWorkflowId(id);
          setWorkflowDialogOpen(true);
        }}
        onNewWorkflow={() => {
          setWorkflowToEdit(null);
          setWorkflowDialogOpen(true);
        }}
        onDeleteWorkflow={handleDeleteWorkflow}
      />
      <Dialog
        isOpen={dialogOpen && Boolean(getFileToShow())}
        setIsOpen={setDialogOpen}
        onClose={handleDialogClose}
        header={t("library.documentInfo")}
        headerClassName="sm:px-4 px-2 py-3 !border-none !mb-0"
        className="!h-screen !max-h-[100vh] !w-[90vw] !max-w-[90vw] !p-0"
      >
        <FileView file={getFileToShow()} />
      </Dialog>
      <Dialog
        isOpen={deepExtractDialogOpen && Boolean(getFileToShow(deepExtract))}
        setIsOpen={setDeepExtractDialogOpen}
        onClose={() => handleDialogClose("deepExtract")}
        header={t("library.aiDeepExtract")}
        headerClassName="sm:px-4 px-2 py-3 !border-none !mb-0"
        className="!h-screen !max-h-[100vh] !w-[90vw] !max-w-[90vw] !p-0"
      >
        <DeepExtractFileView file={getFileToShow(deepExtract)} />
      </Dialog>
      <WorkflowDialog
        isOpen={isWorkflowDialogOpen}
        onClose={handleWorkflowDialogClose}
        initialData={workflowToEdit}
        onSubmit={handleWorkflowSubmit}
        loading={workflowLoading}
      />
    </>
  );
};

export default FileOrFolderView;
